import React from 'react';
import { FiArrowDownRight, FiArrowUpRight, FiMinus } from 'react-icons/fi';

// Single-row KPI strip. Tiles are short and dense: label, value, and either
// a period-over-period delta or a one-line context note. Clicking a tile
// switches the canvas to the matching tab.

function Delta({ pct, invert = false }) {
  if (pct === null || pct === undefined) {
    return <span className="text-[10.5px] text-zinc-400">no prior data</span>;
  }
  const rounded = Math.round(pct);
  if (rounded === 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-[10.5px] font-medium text-zinc-500">
        <FiMinus className="h-3 w-3" /> flat vs prior
      </span>
    );
  }
  const good = invert ? rounded < 0 : rounded > 0;
  const Icon = rounded > 0 ? FiArrowUpRight : FiArrowDownRight;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10.5px] font-semibold tabular-nums ${good ? 'text-emerald-700' : 'text-rose-600'}`}>
      <Icon className="h-3 w-3" />
      {Math.abs(rounded)}% vs prior
    </span>
  );
}

export default function KpiBar({ metrics = [], activeId, onSelect, loading = false }) {
  return (
    <div className="grid grid-cols-2 gap-2 laptop:grid-cols-5">
      {metrics.map((m) => {
        const active = activeId === m.id;
        return (
          <button
            key={m.id}
            type="button"
            onClick={() => onSelect?.(m)}
            title={m.hint || ''}
            aria-pressed={active}
            className={`group relative flex h-[60px] w-full flex-col justify-center rounded-xl border bg-white px-3 text-left transition-colors duration-150 motion-reduce:transition-none ${
              active ? 'border-zinc-900' : 'border-zinc-200 hover:border-zinc-300'
            }`}
          >
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">{m.label}</span>
              {m.badge ? (
                <span className="rounded bg-zinc-100 px-1 py-0.5 text-[9px] font-semibold text-zinc-600">{m.badge}</span>
              ) : null}
            </div>
            <div className="mt-0.5 flex items-baseline justify-between gap-2">
              <span className={`text-[17px] font-semibold tabular-nums leading-none tracking-tight text-zinc-900 ${loading ? 'opacity-50' : ''}`}>
                {m.value}
              </span>
              {m.delta !== undefined ? (
                <Delta pct={m.delta} invert={m.invertDelta} />
              ) : m.sublabel ? (
                <span className="truncate text-[10.5px] text-zinc-500">{m.sublabel}</span>
              ) : null}
            </div>
            <span
              aria-hidden
              className={`absolute inset-x-3 bottom-0 h-[2px] origin-left rounded-t-full bg-zinc-900 transition-transform duration-200 motion-reduce:transition-none ${
                active ? 'scale-x-100' : 'scale-x-0'
              }`}
            />
          </button>
        );
      })}
    </div>
  );
}
